import React, { memo, useEffect, useRef, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { COLORS, FONT } from "../../shared/styles";

const labelSx = {
  color: COLORS.textSecondary,
  fontFamily: FONT.family,
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "normal",
  mb: "8px",
};

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "40px",
    borderRadius: "8px",
    fontFamily: FONT.family,
    fontSize: "14px",
    color: COLORS.textPrimary,
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    background: COLORS.white,
    "& fieldset": { borderColor: "#B7C2CC" },
    "&:hover fieldset": { borderColor: "#B7C2CC" },
    "&.Mui-focused fieldset": { borderColor: COLORS.primary },
  },
  "& .MuiOutlinedInput-input": { py: 0, px: "12px" },
};

const DEBOUNCE_MS = 80;

function normalizeHex(value) {
  return value.startsWith("#") ? value : `#${value}`;
}

function PrimaryColorField({ value, onChange }) {
  const [localColor, setLocalColor] = useState(value);
  const debounceRef = useRef(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!isDraggingRef.current) {
      setLocalColor(value);
    }
  }, [value]);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  const commitColor = (nextColor, immediate = false) => {
    const hex = normalizeHex(nextColor);
    setLocalColor(hex);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (immediate) {
      isDraggingRef.current = false;
      onChange(hex);
      return;
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      isDraggingRef.current = false;
      onChange(hex);
    }, DEBOUNCE_MS);
  };

  const flushPendingColor = () => {
    if (!debounceRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = null;
    isDraggingRef.current = false;
    onChange(localColor);
  };

  const handlePickerChange = (nextColor) => {
    isDraggingRef.current = true;
    commitColor(nextColor);
  };

  const handleTextChange = (nextColor) => {
    commitColor(nextColor, true);
  };

  return (
    <Box>
      <Typography sx={labelSx}>Primary Color</Typography>
      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Box
          component="label"
          sx={{
            width: 40,
            height: 40,
            borderRadius: "8px",
            border: "1px solid #B7C2CC",
            background: localColor,
            boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
            flexShrink: 0,
            cursor: "pointer",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <input
            type="color"
            value={localColor}
            onChange={(e) => handlePickerChange(e.target.value)}
            onBlur={flushPendingColor}
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0,
              cursor: "pointer",
              width: "100%",
              height: "100%",
            }}
          />
        </Box>
        <TextField
          value={localColor}
          onChange={(e) => handleTextChange(e.target.value)}
          sx={{ ...inputSx, flex: 1 }}
        />
      </Box>
    </Box>
  );
}

export default memo(PrimaryColorField);
